import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Loader2, Database, CheckCircle, AlertTriangle } from 'lucide-react'
import ApiService from '@/services/api'
import type { Restaurant } from '@/types'

interface DatabaseAssignmentProps {
  restaurant: Restaurant
  onUpdate: (restaurant: Restaurant) => void
}

export default function DatabaseAssignment({ restaurant, onUpdate }: DatabaseAssignmentProps) {
  const [databaseName, setDatabaseName] = useState(restaurant.databaseName || '')
  const [isAssigning, setIsAssigning] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState<{
    connected: boolean
    collections: string[]
  } | null>(null)
  const [error, setError] = useState('')

  const handleTestConnection = async () => {
    if (!databaseName.trim()) {
      setError('Please enter a database name')
      return
    }

    setIsTesting(true)
    setError('')
    setTestResult(null)

    try {
      const response = await ApiService.testDatabaseConnection(databaseName.trim())
      if (response.success) {
        setTestResult(response.data)
      } else {
        setError(response.message || 'Failed to test connection')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Connection test failed')
    } finally {
      setIsTesting(false)
    }
  }

  const handleAssignDatabase = async () => {
    if (!databaseName.trim()) {
      setError('Please enter a database name')
      return
    }

    setIsAssigning(true)
    setError('')

    try {
      const response = await ApiService.assignRestaurantDatabase(restaurant._id, databaseName.trim())
      if (response.success) {
        onUpdate(response.data)
        setError('')
      } else {
        setError(response.message || 'Failed to assign database')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Database assignment failed')
    } finally {
      setIsAssigning(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Database className="h-5 w-5" />
          <CardTitle className="text-base">Database Assignment</CardTitle>
        </div>
        <CardDescription>
          Assign a MongoDB database to this restaurant for data isolation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Assignment */}
        <div>
          <Label className="text-sm font-medium">Current Database</Label>
          <div className="mt-1">
            {restaurant.databaseName ? (
              <Badge variant="success" className="flex items-center space-x-1 w-fit">
                <CheckCircle className="h-3 w-3" />
                <span>{restaurant.databaseName}</span>
              </Badge>
            ) : (
              <Badge variant="destructive" className="flex items-center space-x-1 w-fit">
                <AlertTriangle className="h-3 w-3" />
                <span>Not Assigned</span>
              </Badge>
            )}
          </div>
        </div>

        {/* Database Name Input */}
        <div className="space-y-2">
          <Label htmlFor="databaseName">Database Name</Label>
          <Input
            id="databaseName"
            placeholder="e.g., , ordore-restaurant2"
            value={databaseName}
            onChange={(e) => setDatabaseName(e.target.value)}
            disabled={isAssigning || isTesting}
          />
          <p className="text-xs text-muted-foreground">
            Format: ordore-[restaurant-identifier] (e.g., , ordore-mumbai-kitchen)
          </p>
        </div>

        {/* Test Connection Button */}
        <Button
          type="button"
          variant="outline"
          onClick={handleTestConnection}
          disabled={isTesting || isAssigning || !databaseName.trim()}
          className="w-full"
        >
          {isTesting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Testing Connection...
            </>
          ) : (
            <>
              <Database className="mr-2 h-4 w-4" />
              Test Database Connection
            </>
          )}
        </Button>

        {/* Test Results */}
        {testResult && (
          <Alert variant={testResult.connected ? "default" : "destructive"}>
            <div className="flex items-center space-x-2">
              {testResult.connected ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <AlertDescription className="flex-1">
                {testResult.connected ? (
                  <div>
                    <p className="font-medium">Connection Successful!</p>
                    <p className="text-sm mt-1">
                      Found collections: {testResult.collections.join(', ') || 'None'}
                    </p>
                    {testResult.collections.includes('orderbills') && testResult.collections.includes('foods') && (
                      <p className="text-sm text-green-600 mt-1">
                        ✓ Required collections (orderbills, foods) found
                      </p>
                    )}
                  </div>
                ) : (
                  'Connection failed. Please check the database name.'
                )}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Assign Database Button */}
        <Button
          onClick={handleAssignDatabase}
          disabled={isAssigning || isTesting || !databaseName.trim()}
          className="w-full"
        >
          {isAssigning ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Assigning Database...
            </>
          ) : (
            <>
              <Database className="mr-2 h-4 w-4" />
              {restaurant.databaseName ? 'Update Database Assignment' : 'Assign Database'}
            </>
          )}
        </Button>

        {/* Connection Info */}
        <div className="text-xs text-muted-foreground p-3 bg-muted rounded-md">
          <p className="font-medium mb-2">MongoDB Connection Details:</p>
          <p>Cluster: ordorecone.equ25.mongodb.net</p>
          <p>Connection: mongodb+srv://krishnarajmavila:oTxhfAchO2rErhS3@ordorecone.equ25.mongodb.net/[database-name]</p>
          <p className="mt-2">
            Each restaurant will have its own database containing:
          </p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>orderbills - Restaurant orders and transactions</li>
            <li>foods - Menu items and pricing</li>
            <li>Other restaurant-specific collections</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}